import React, { memo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import { DriverLicensePage } from 'src/pages/client/docs/DriverLicensePage'
import { VehicleRegistrationPage } from 'src/pages/client/docs/VehicleRegistrationPage'
import { VaccinationPage } from 'src/pages/client/docs/VaccinationPage'
import { InsuranceBeneficiaryPage } from 'src/pages/client/docs/InsuranceBeneficiaryPage'
import { OnboardingSuccessPage } from '../../OnboardingSuccessPage'
import { LoadingPage } from '../../LoadingPage'
import { SignContractPage } from './sign-contract/SignContractPage'
import { OnboardingFailedPage } from 'src/pages/OnboardingFailedPage'
import { MVRPage } from './MVRPage'
import { FormPage } from 'src/pages/client/docs/FormPage'
import { EIN } from 'src/components/ein/EIN'
import { VehicleInsurance } from 'src/components/vehicle-insurance/VehicleInsurance'
import { InsuranceCoverage } from 'src/components/insurance-coverage/InsuranceCoverage'
import { MCNumber } from 'src/components/mc-number'
import { CollectDataStepStatus, CollectDataStepType } from 'src/constants/collect-data/enums'
import {
  getCurrentCollectDataStep,
  getIsCollectDataFailed,
  getOnlyDelayedStepsLeft,
} from 'src/selectors/collectData'
import { CollectDataConfigPageWrapper } from 'src/pages/shared/CollectDataConfigPageWrapper'
import { getPartner } from 'src/selectors/partner'
import { getDriver } from 'src/selectors/driver'
import { SelectRecollectionStep } from 'src/constants/actionTypes'
import { CLIENT_ROUTES } from 'src/constants/routes'
import { DataCollectionSource } from 'src/constants/shared'
import { LMSTraining } from 'src/components/lms-training/LMSTraining'
import { DOTNumber } from 'src/components/dot-number/DOTNumber'
import { PIICollectionPage } from 'src/pages/client/docs/PIICollectionPage'
import { InsuranceDeclaration } from 'src/components/insurance-declaration/InsuranceDeclaration'
import { BankPaymentPage } from './BankPaymentPage'
import { BGC } from 'src/components/bgc/BGC'
import { useQuery } from 'src/utils/hooks/useQuery'
import {
  clearPendingReturnStep,
  getPendingReturnStepSource,
  PendingReturnStepSource,
  readPendingReturnStepId,
} from 'src/utils/pendingReturnStep'

interface PendingReturn {
  stepId: string
  source: PendingReturnStepSource
}

const stepsPageMap = {
  [CollectDataStepType.DriverLicense]: DriverLicensePage,
  [CollectDataStepType.VehicleInsurance]: VehicleInsurance,
  [CollectDataStepType.InsuranceDeclaration]: InsuranceDeclaration,
  [CollectDataStepType.VehicleRegistration]: VehicleRegistrationPage,
  [CollectDataStepType.Vaccination]: VaccinationPage,
  [CollectDataStepType.BGC]: BGC,
  [CollectDataStepType.InsuranceBeneficiary]: InsuranceBeneficiaryPage,
  [CollectDataStepType.SignContract]: SignContractPage,
  [CollectDataStepType.MVR]: MVRPage,
  [CollectDataStepType.EIN]: EIN,
  [CollectDataStepType.Form]: FormPage,
  [CollectDataStepType.InsuranceCoverage]: InsuranceCoverage,
  [CollectDataStepType.LMSTraining]: LMSTraining,
  [CollectDataStepType.MCNumber]: MCNumber,
  [CollectDataStepType.DOTNumber]: DOTNumber,
  [CollectDataStepType.PaymentMethod]: BankPaymentPage,
  [CollectDataStepType.PIICollection]: PIICollectionPage,
}

const ClientDocsPageComponent = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getQuery } = useQuery()

  const currentStep = useSelector(getCurrentCollectDataStep)
  const onlyDelayedStepsLeft = useSelector(getOnlyDelayedStepsLeft)
  const driver = useSelector(getDriver)
  const isOnboardingFailed = useSelector(getIsCollectDataFailed)
  const partner = useSelector(getPartner)

  const isRecollection = driver?.collectDataFlow === DataCollectionSource.Recollection

  const returnEvent = getQuery('event')
  const returnStepId = getQuery('stepId')

  // DocuSign's "sign in a new tab" fallback and the LMS course both return to this page in a fresh
  // page load, where no recollection step is selected yet. Restore the step that sent the
  // contractor away so its component can handle the `event` query instead of this page redirecting
  // home. A `stepId` in the return URL wins; the localStorage entry covers returns that can't
  // carry one (DocuSign's return URL is built by the backend).
  const [pendingReturn, setPendingReturn] = useState<PendingReturn | null>(() => {
    if (!returnEvent) {
      return null
    }

    const source = getPendingReturnStepSource(returnEvent)
    const stepId = returnStepId || readPendingReturnStepId(source)

    return stepId ? { stepId, source } : null
  })

  useEffect(() => {
    if (!pendingReturn || !driver) {
      return
    }

    clearPendingReturnStep(pendingReturn.source)

    if (isRecollection) {
      dispatch(SelectRecollectionStep.success(pendingReturn.stepId))
    }

    setPendingReturn(null)
  }, [pendingReturn, driver, isRecollection, dispatch])

  useEffect(() => {
    if (!isRecollection || !currentStep) {
      return
    }

    if (currentStep.status !== CollectDataStepStatus.Created) {
      dispatch(SelectRecollectionStep.success(null))
      navigate(CLIENT_ROUTES.HOME_URL)
    }
  }, [isRecollection, currentStep, dispatch, navigate])

  if (!driver) {
    return <LoadingPage flow="client" />
  }

  if (isRecollection) {
    if (pendingReturn) {
      return <LoadingPage flow="client" />
    }

    if (!currentStep || currentStep.status !== CollectDataStepStatus.Created) {
      return <Navigate to={CLIENT_ROUTES.HOME_URL} replace />
    }

    const RecollectionStepComponent = stepsPageMap[currentStep.type]

    if (!RecollectionStepComponent) {
      return <Navigate to={CLIENT_ROUTES.HOME_URL} replace />
    }

    return <RecollectionStepComponent key={currentStep.id} />
  }

  if (isOnboardingFailed) {
    return <OnboardingFailedPage />
  }

  if (!currentStep || onlyDelayedStepsLeft) {
    return (
      <OnboardingSuccessPage
        title={
          onlyDelayedStepsLeft
            ? partner?.gigsafeConfig?.onboardingSuccessScreenDelayedTitle
            : partner?.gigsafeConfig?.onboardingSuccessScreenTitle
        }
        description={
          onlyDelayedStepsLeft
            ? partner?.gigsafeConfig?.onboardingSuccessScreenDelayedDescription
            : partner?.gigsafeConfig?.onboardingSuccessScreenDescription
        }
      />
    )
  }

  const StepComponent = stepsPageMap[currentStep.type]

  if (StepComponent) {
    return (
      <CollectDataConfigPageWrapper>
        <StepComponent key={currentStep.id} />
      </CollectDataConfigPageWrapper>
    )
  }

  return (
    <OnboardingSuccessPage
      title={partner?.gigsafeConfig?.onboardingSuccessScreenTitle}
      description={partner?.gigsafeConfig?.onboardingSuccessScreenDescription}
    />
  )
}

export const ClientDocsPage = memo(ClientDocsPageComponent)
